import { GameMode } from '../types/GameMode';
import { getChallengeStage } from '../constants/challengeConfig';

/** getMonsterIdsForPreload が必要とする BattleSceneData の最小サブセット */
export interface BattlePreloadInput {
  mode?: GameMode;
  monsterId?: string;
  enemyMonsterId?: string;
  playerMonster?: { id: string };
  enemyMonster?: { id: string };
  playerParty?: { monsterId: string }[];
  enemyParty?: { monsterId: string }[];
  playerPartyIndex?: number;
  enemyPartyIndex?: number;
  stageNumber?: number;
  isBossStage?: boolean;
  isFormChange?: boolean;
}

/**
 * バトルで使用するモンスター ID を data から収集し、
 * availableMonsterIds に含まれるものだけを返す。
 *
 * preload() で必要なスプライトのみをロードするために使用する。
 *
 * ボスステージは enemyPartyIndex で現在の形態のみを対象にすることで
 * 一度に読み込むファイル数を抑え、iOS のメモリ制限超過を防ぐ。
 */
export function getMonsterIdsForPreload(
  data: BattlePreloadInput | undefined,
  availableMonsterIds: readonly string[],
): string[] {
  if (!data) return [];

  const ids: string[] = [];

  // 文字列 ID（FREE_CPU・CHALLENGE 等）
  if (data.monsterId) ids.push(data.monsterId);
  if (data.enemyMonsterId) ids.push(data.enemyMonsterId);

  // Monster オブジェクト（ネットワーク・サバイバル等）
  if (data.playerMonster) ids.push(data.playerMonster.id);
  if (data.enemyMonster) ids.push(data.enemyMonster.id);

  // プレイヤーパーティ: 全員分ロード（3vs3で誰でも出場しうる）
  data.playerParty?.forEach(m => ids.push(m.monsterId));

  // 敵パーティ: ボスステージは現在の形態のみ、通常3vs3は全員
  if (data.enemyParty) {
    if (data.isBossStage && data.enemyPartyIndex != null) {
      const current = data.enemyParty[data.enemyPartyIndex];
      if (current) ids.push(current.monsterId);
    } else {
      data.enemyParty.forEach(m => ids.push(m.monsterId));
    }
  }

  // CHALLENGE 非ボスステージ: ステージ設定から敵 ID を取得
  if (data.mode === GameMode.CHALLENGE && !data.isBossStage && data.stageNumber != null) {
    const stage = getChallengeStage(data.stageNumber);
    if (stage) ids.push(stage.enemyMonsterId);
  }

  return [...new Set(ids)].filter(id => availableMonsterIds.includes(id));
}
